import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiClock, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import ShareResolveDetailView from '../../components/ShareResolveDetailView';
import {
  resolveCategoryShare,
  importCategoryShare,
  importBuiltinCategoryShare,
  categoryChannelName,
  NEEDS_APPROVAL,
  CATEGORY_ASSIGNEE_AI,
  CATEGORY_ASSIGNEE_DW_AGENT,
  CATEGORY_MODE_AUTO,
  CATEGORY_MODE_MANUAL,
  CATEGORY_MODE_DISABLED,
  type CategoryShareSnapshot,
  type ImportedCategory,
  type CategoryKind,
  type BuiltinCategoryCurrentSetup,
  type AppliedBuiltinSetup,
  type CategoryShareApiError,
} from '../../services/categoryShareApi';
import { listDwAgents } from '../claudeagents/claudeAgentApi';
import { forgetShared } from '../../utils/inFlight';
import { ROUTES } from '../../app/routes';
import '../../styles/MyTools.css';

type Status = 'loading' | 'preview' | 'importing' | 'done' | 'error';

const MATCH_ALL = 1;

// Same names and order as the Command Center panel's mode toggles. Wait for
// Human Approval is the default: it is what a category with no saved mode already
// does in the task pipeline, with a person approving every task it creates.
const RUN_MODES: { value: number; label: string; hint: string }[] = [
  {
    value: CATEGORY_MODE_MANUAL,
    label: 'Wait for Human Approval',
    hint: 'New tasks wait in To Do until someone approves them.',
  },
  {
    value: CATEGORY_MODE_AUTO,
    label: 'Auto Run',
    hint: 'Tasks assigned to an agent start running as soon as they arrive.',
  },
  {
    value: CATEGORY_MODE_DISABLED,
    label: 'Disabled',
    hint: 'No tasks are created for it until you turn it on.',
  },
];

const runModeLabel = (mode: number) => RUN_MODES.find((m) => m.value === mode)?.label ?? 'Wait for Human Approval';

// Category-level counterpart to ShareAgentResolvePage: opens a magic link
// minted from the Command Center's Share button, previews the frozen category
// definition, and — only once the person confirms — creates their own
// independent copy (a new kanban_custom_sop_category row of theirs) with the
// run mode they pick here. The sharer's agent assignment can't travel (agent
// ids are per user), so when the shared category was assigned to an agent this
// offers the person their OWN agents instead, pre-selecting one with the same
// name when there is one.
//
// A link to a BUILT-IN category carries the sharer's setup of it on one
// channel instead: confirming writes that setup as the person's own (their run
// mode is untouched), so there is no name or run-mode field. When they already
// have a prompt on that category the page says so up front and the Confirm
// button stays off until they tick "replace my current setup" — the server
// refuses the import (409 needs_approval) without that flag anyway.
const ShareCategoryResolvePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState('');
  const [snapshot, setSnapshot] = useState<CategoryShareSnapshot | null>(null);
  const [kind, setKind] = useState<CategoryKind>('custom');
  // Built-in links: the person's current setup of that category + channel.
  const [existing, setExisting] = useState<BuiltinCategoryCurrentSetup | null>(null);
  const [channelName, setChannelName] = useState('Email');
  const [replaceConfirmed, setReplaceConfirmed] = useState(false);
  const [builtinResult, setBuiltinResult] = useState<AppliedBuiltinSetup | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [nameError, setNameError] = useState('');
  const [runMode, setRunMode] = useState<number>(CATEGORY_MODE_MANUAL);
  const [trusted, setTrusted] = useState(false);
  const [result, setResult] = useState<ImportedCategory | null>(null);
  // The person's own Digital Workforce agents, for the "Assign to" picker.
  const [myAgents, setMyAgents] = useState<{ id: string; name: string }[]>([]);
  // '' = keep the user-neutral assignee (the User); otherwise one of myAgents' ids.
  const [assigneeAgentId, setAssigneeAgentId] = useState('');
  const [agentPreselected, setAgentPreselected] = useState(false);

  // Loading -> Preview only resolves the share and shows what it contains —
  // nothing is created until handleConfirm.
  const load = useCallback(async () => {
    if (!token) return;
    setStatus('loading');
    setError('');
    try {
      const share = await resolveCategoryShare(token);
      const resolved = share.snapshot;
      setSnapshot(resolved);
      setKind(share.kind);
      setExisting(share.existing ?? null);
      setChannelName(share.channel_name ?? categoryChannelName(resolved.channel));
      setReplaceConfirmed(false);
      setCategoryName(resolved.name);
      setStatus('preview');
    } catch (err: any) {
      setError(err.message || 'This link could not be opened.');
      setStatus('error');
    }
  }, [token]);
  useEffect(() => {
    load();
  }, [load]);

  // Only needed when the shared category was assigned to an agent — the picker
  // is hidden otherwise and the copy keeps the shared, user-neutral assignee.
  const wantsAgent = !!snapshot && snapshot.assignee_type === CATEGORY_ASSIGNEE_DW_AGENT;
  useEffect(() => {
    if (!wantsAgent) return;
    let cancelled = false;
    listDwAgents(false, true)
      .then((records) => {
        if (cancelled) return;
        const active = records
          .filter((r) => !r.is_archived && typeof r.agent_id === 'string' && r.name)
          .map((r) => ({ id: r.agent_id, name: r.name.trim() }));
        setMyAgents(active);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [wantsAgent]);

  // Pre-select the person's agent with the same name as the sharer's, once —
  // never stomping a choice they've already made in the picker.
  useEffect(() => {
    if (!wantsAgent || agentPreselected || !snapshot || myAgents.length === 0) return;
    const wanted = (snapshot.agent_name || '').trim().toLowerCase();
    const match = wanted ? myAgents.find((a) => a.name.toLowerCase() === wanted) : undefined;
    if (match) setAssigneeAgentId(match.id);
    setAgentPreselected(true);
  }, [wantsAgent, agentPreselected, snapshot, myAgents]);

  const isBuiltin = kind === 'builtin';
  const needsReplace = isBuiltin && !!existing?.has_prompt;

  const handleConfirmBuiltin = async () => {
    if (!token || !snapshot) return;
    if (needsReplace && !replaceConfirmed) return;
    setError('');
    const agent = assigneeAgentId ? myAgents.find((a) => a.id === assigneeAgentId) : undefined;
    try {
      setStatus('importing');
      const applied = await importBuiltinCategoryShare(token, {
        replace: replaceConfirmed,
        agentId: agent?.id,
        agentName: agent?.name,
      });
      setBuiltinResult(applied);
      setStatus('done');
    } catch (err: any) {
      const apiError = err as CategoryShareApiError;
      // The server found a prompt the page did not know about (added since the
      // link was opened): show the replacement notice instead of failing.
      if (apiError?.code === NEEDS_APPROVAL) {
        setExisting((prev) => ({ ...(prev ?? emptySetup), has_prompt: true }));
        setReplaceConfirmed(false);
        setError(apiError.message || '');
        setStatus('preview');
        return;
      }
      setError(apiError?.message || 'This link could not be opened.');
      setStatus('error');
    }
  };

  const handleConfirm = async () => {
    if (isBuiltin) return handleConfirmBuiltin();
    if (!token || !snapshot) return;
    const trimmedName = categoryName.trim();
    if (!trimmedName) {
      setNameError('Give this category a name.');
      return;
    }
    setNameError('');
    setError('');
    const agent = assigneeAgentId ? myAgents.find((a) => a.id === assigneeAgentId) : undefined;
    try {
      setStatus('importing');
      const imported = await importCategoryShare(token, {
        name: trimmedName,
        mode: runMode,
        agentId: agent?.id,
        agentName: agent?.name,
      });
      // The config panel briefly reuses its cached custom-category list — drop
      // it so the new copy shows up as soon as the person goes there.
      forgetShared('kanbanCustomCategories|');
      setResult(imported);
      setStatus('done');
    } catch (err: any) {
      const message: string = err.message || 'This link could not be opened.';
      // Name collision (409) is recoverable — back to the preview with the name
      // field flagged, so the person can just edit the name and try again.
      if (message.toLowerCase().includes('choose a different name')) {
        setNameError(message);
        setStatus('preview');
      } else {
        setError(message);
        setStatus('error');
      }
    }
  };

  const describeAssignee = (assigneeType: number, agentName: string) => {
    if (assigneeType === CATEGORY_ASSIGNEE_DW_AGENT) {
      return agentName ? `Digital Workforce agent "${agentName}"` : 'Digital Workforce agent';
    }
    if (assigneeType === CATEGORY_ASSIGNEE_AI) return 'AI Agent';
    return 'User';
  };

  if (status === 'preview' && snapshot && token) {
    const triggerOnAll = snapshot.category_match_type === MATCH_ALL;
    const selectedRunMode = RUN_MODES.find((m) => m.value === runMode) ?? RUN_MODES[0];
    const confirmDisabled = !trusted || (needsReplace && !replaceConfirmed);
    return (
      <div className='mt-page'>
        <ShareResolveDetailView
          icon='🗂️'
          name={snapshot.name}
          typeLabel={isBuiltin ? `Built-in Command Center category · ${channelName} setup` : 'Command Center category'}
          description={snapshot.description || undefined}
        >
          {error && (
            <p role='alert' style={{ color: '#f87171', fontSize: 12.5, margin: '0 0 12px' }}>
              {error}
            </p>
          )}
          {isBuiltin ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: 12.5, margin: '0 0 14px' }}>
              This is someone&apos;s setup of the built-in <strong>{snapshot.name}</strong> category for {channelName}:
              its labels, prompts, trigger mode and assignee. Adding it writes your own setup of that category in
              your Command Center. Your run mode for it stays as it is, and you can fine-tune everything later.
            </p>
          ) : (
            <p style={{ color: 'var(--text-secondary)', fontSize: 12.5, margin: '0 0 14px' }}>
              You&apos;ll get your own independent copy of this category in your Command Center — the trigger,
              labels and prompts, never a reference to the original. You choose its run mode and who it&apos;s
              assigned to below, and you can change both later.
            </p>
          )}
          {needsReplace && existing && (
            <div
              role='note'
              style={{
                margin: '0 0 14px',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid rgba(245,158,11,0.45)',
                background: 'rgba(245,158,11,0.08)',
                color: 'var(--text-secondary)',
                fontSize: 12.5,
                lineHeight: 1.5,
              }}
            >
              <strong style={{ color: 'var(--text-primary)' }}>You already have a prompt on this category.</strong>{' '}
              Adding this setup replaces your current {channelName} labels, prompts and assignee for{' '}
              {snapshot.name} with the shared ones.
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Show my current setup</summary>
                <dl style={{ ...definitionListStyle, marginTop: 8 }}>
                  {existing.trigger_label && (
                    <>
                      <dt style={termStyle}>Trigger label</dt>
                      <dd style={valueStyle}>{existing.trigger_label}</dd>
                    </>
                  )}
                  {existing.completion_label && (
                    <>
                      <dt style={termStyle}>Completion label</dt>
                      <dd style={valueStyle}>{existing.completion_label}</dd>
                    </>
                  )}
                  {existing.identification_prompt && (
                    <>
                      <dt style={termStyle}>Identification prompt</dt>
                      <dd style={{ ...valueStyle, whiteSpace: 'pre-wrap' }}>{existing.identification_prompt}</dd>
                    </>
                  )}
                  {existing.execution_prompt && (
                    <>
                      <dt style={termStyle}>Execution prompt</dt>
                      <dd style={{ ...valueStyle, whiteSpace: 'pre-wrap' }}>{existing.execution_prompt}</dd>
                    </>
                  )}
                  <dt style={termStyle}>Assigned to</dt>
                  <dd style={valueStyle}>{describeAssignee(existing.assignee_type, existing.agent_name)}</dd>
                </dl>
              </details>
            </div>
          )}
          <h4 className='mt-detail-section-title'>{isBuiltin ? 'Shared setup' : 'Category definition'}</h4>
          <dl style={definitionListStyle}>
            <dt style={termStyle}>Trigger on</dt>
            <dd style={valueStyle}>{triggerOnAll ? 'All tasks (identification prompt decides)' : 'A specific inbox label'}</dd>
            {!triggerOnAll && (
              <>
                <dt style={termStyle}>Trigger label</dt>
                <dd style={valueStyle}>{snapshot.trigger_label || <Empty />}</dd>
              </>
            )}
            <dt style={termStyle}>Completion label</dt>
            <dd style={valueStyle}>{snapshot.completion_label || <Empty />}</dd>
            {triggerOnAll && (
              <>
                <dt style={termStyle}>Identification prompt</dt>
                <dd style={{ ...valueStyle, whiteSpace: 'pre-wrap' }}>{snapshot.identification_prompt || <Empty />}</dd>
              </>
            )}
            <dt style={termStyle}>Execution prompt</dt>
            <dd style={{ ...valueStyle, whiteSpace: 'pre-wrap' }}>{snapshot.execution_prompt || <Empty />}</dd>
            <dt style={termStyle}>Shared as assigned to</dt>
            <dd style={valueStyle}>{describeAssignee(snapshot.assignee_type, snapshot.agent_name)}</dd>
          </dl>
        </ShareResolveDetailView>

        <div
          className='mt-detail-card'
          style={{ marginTop: 14, marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          {!isBuiltin && (
            <div>
              <label htmlFor='share-category-name' style={fieldLabelStyle}>
                Name your copy
              </label>
              <input
                id='share-category-name'
                className='ca-fp-editor__textarea'
                style={fieldStyle}
                value={categoryName}
                onChange={(e) => {
                  setCategoryName(e.target.value);
                  setNameError('');
                }}
              />
              {nameError && <p style={{ color: '#f87171', fontSize: 12, margin: '6px 0 0' }}>{nameError}</p>}
            </div>
          )}

          {!isBuiltin && (
            <div>
              <label htmlFor='share-category-mode' style={fieldLabelStyle}>
                Run mode
              </label>
              <select
                id='share-category-mode'
                className='ca-fp-editor__textarea'
                style={fieldStyle}
                value={String(runMode)}
                onChange={(e) => setRunMode(Number(e.target.value))}
              >
                {RUN_MODES.map((m) => (
                  <option key={m.value} value={String(m.value)}>
                    {m.label}
                  </option>
                ))}
              </select>
              <p style={fieldHintStyle}>{selectedRunMode.hint}</p>
            </div>
          )}

          {wantsAgent && (
            <div>
              <label htmlFor='share-category-assignee' style={fieldLabelStyle}>
                Assign to
              </label>
              <select
                id='share-category-assignee'
                className='ca-fp-editor__textarea'
                style={fieldStyle}
                value={assigneeAgentId}
                onChange={(e) => {
                  setAssigneeAgentId(e.target.value);
                  setAgentPreselected(true);
                }}
              >
                <option value=''>Me (User)</option>
                {myAgents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
              <p style={fieldHintStyle}>
                The sharer&apos;s agent can&apos;t come along — agents belong to their own account — so pick one of
                yours, or keep the category assigned to you.
              </p>
            </div>
          )}

          {needsReplace && (
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                fontSize: 12.5,
                color: 'var(--text-secondary)',
                lineHeight: 1.5,
                cursor: 'pointer',
              }}
            >
              <input
                type='checkbox'
                checked={replaceConfirmed}
                onChange={(e) => setReplaceConfirmed(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              Replace my current {channelName} setup of {snapshot.name} with this one.
            </label>
          )}

          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              fontSize: 12.5,
              color: 'var(--text-secondary)',
              lineHeight: 1.5,
              cursor: 'pointer',
            }}
          >
            <input
              type='checkbox'
              checked={trusted}
              onChange={(e) => setTrusted(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            I trust this community shared item and understand it comes from another agency in the Momentum AMP
            community, not from Momentum AMP directly.
          </label>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type='button' className='mt-btn mt-btn--primary' disabled={confirmDisabled} onClick={handleConfirm}>
              {needsReplace ? 'Replace my setup' : 'Add to Command Center'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='mt-page'>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          textAlign: 'center',
          gap: 14,
        }}
      >
        {status === 'loading' && (
          <>
            <FiClock size={32} style={{ color: 'var(--text-secondary)' }} />
            <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Opening shared category…</p>
          </>
        )}

        {status === 'importing' && (
          <>
            <FiClock size={32} style={{ color: 'var(--text-secondary)' }} />
            <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
              {isBuiltin ? 'Applying the setup in your Command Center…' : 'Adding the category to your Command Center…'}
            </p>
          </>
        )}

        {status === 'done' && isBuiltin && builtinResult && (
          <>
            <FiCheckCircle size={32} style={{ color: '#4ade80' }} />
            <h2 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: 0 }}>
              &quot;{builtinResult.name}&quot; setup {builtinResult.replaced ? 'replaced' : 'added'}
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, maxWidth: 420 }}>
              Your {categoryChannelName(builtinResult.channel)} setup of {builtinResult.name} now matches the shared
              one, assigned to{' '}
              {builtinResult.assignee_type === CATEGORY_ASSIGNEE_DW_AGENT && builtinResult.agent_name
                ? builtinResult.agent_name
                : builtinResult.assignee_type === CATEGORY_ASSIGNEE_AI
                  ? 'the AI Agent'
                  : 'you'}
              . Your run mode wasn&apos;t changed. Fine-tune the SOP from the Command Center configuration panel.
            </p>
            <button
              type='button'
              className='mt-btn mt-btn--primary'
              style={{ marginTop: 8 }}
              onClick={() => navigate(ROUTES.MOMENTUM_COMMAND)}
            >
              Go to Command Center
            </button>
          </>
        )}

        {status === 'done' && !isBuiltin && result && (
          <>
            <FiCheckCircle size={32} style={{ color: '#4ade80' }} />
            <h2 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: 0 }}>
              &quot;{result.name}&quot; added
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, maxWidth: 420 }}>
              Your own copy of this category is now in your Command Center, assigned to{' '}
              {result.assignee_type === CATEGORY_ASSIGNEE_DW_AGENT && result.agent_name
                ? result.agent_name
                : result.assignee_type === CATEGORY_ASSIGNEE_AI
                  ? 'the AI Agent'
                  : 'you'}
              {' '}and set to {runModeLabel(result.mode)}. Change either one, or fine-tune the SOP, from the
              Command Center configuration panel.
            </p>
            <button
              type='button'
              className='mt-btn mt-btn--primary'
              style={{ marginTop: 8 }}
              onClick={() => navigate(ROUTES.MOMENTUM_COMMAND)}
            >
              Go to Command Center
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <FiAlertTriangle size={32} style={{ color: '#f87171' }} />
            <h2 style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: 0 }}>
              Couldn&apos;t open this link
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, maxWidth: 420 }}>{error}</p>
          </>
        )}
      </div>
    </div>
  );
};

const Empty: React.FC = () => <span style={{ color: 'var(--text-tertiary)' }}>Not set</span>;

const emptySetup: BuiltinCategoryCurrentSetup = {
  has_prompt: false,
  trigger_label: '',
  completion_label: '',
  identification_prompt: '',
  execution_prompt: '',
  category_match_type: 0,
  assignee_type: 0,
  agent_name: '',
};

const definitionListStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(120px, 160px) 1fr',
  columnGap: 14,
  rowGap: 10,
  margin: 0,
};
const termStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  margin: 0,
};
const valueStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--text-primary)',
  margin: 0,
  wordBreak: 'break-word',
  lineHeight: 1.5,
};
const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 6,
};
const fieldStyle: React.CSSProperties = {
  minHeight: 'auto',
  height: 38,
  padding: '8px 12px',
  fontSize: 13,
  width: '100%',
};
const fieldHintStyle: React.CSSProperties = {
  color: 'var(--text-tertiary)',
  fontSize: 11.5,
  margin: '6px 0 0',
};

export default ShareCategoryResolvePage;
